import React, { type ReactNode } from 'react'
import { Card } from 'primereact/card'
import { Button } from 'primereact/button'
import { loginPathFor } from '../../auth/loginPath.js'

interface State { error: Error | null; eventId: string | null }
interface Props { children: ReactNode }

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null, eventId: null }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    const eventId = crypto.randomUUID()
    console.error('[ErrorBoundary]', eventId, error, info)
    // Best-effort POST to /api/errors/report (route added in Task 3)
    void fetch('/api/errors/report', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        eventId,
        message: error.message,
        stack: error.stack,
        componentStack: info.componentStack,
      }),
    }).catch(() => {})  // ignore network errors in the fallback
    this.setState({ eventId })
  }

  reset = (): void => this.setState({ error: null, eventId: null })

  render(): ReactNode {
    if (this.state.error) {
      return (
        <div role="alert" className="min-h-screen flex items-center justify-center p-8">
          <Card className="max-w-md w-full">
            <h1 className="text-xl font-semibold">Something went wrong</h1>
            <p className="text-sm text-gray-600 mt-2">
              The error has been reported. You can try again, or sign out and back in.
            </p>
            <p className="text-xs font-mono text-gray-400 mt-3">
              Reference: {this.state.eventId}
            </p>
            <div className="mt-4 flex gap-2">
              <Button label="Try again" onClick={this.reset} autoFocus />
              <Button label="Sign out" severity="secondary" onClick={() => { window.location.href = loginPathFor(window.location.pathname) }} />
            </div>
          </Card>
        </div>
      )
    }
    return this.props.children
  }
}

export default ErrorBoundary
